import { Component, ComponentClass, ComponentType } from 'react';

import * as Sentry from '@sentry/react';
import { History } from 'history';
import hoistStatics from 'hoist-non-react-statics';
import startCase from 'lodash/startCase';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';

import DebugHelper from '@@/auto/components/DebugHelper';
import ErrorBoundaryView from '@@/auto/components/ErrorBoundaryView';
import { TErrorType } from '@@/auto/containers/ErrorHandler';
import type { TAppStoreState } from '@@/auto/store';
import { captureException } from '@@/auto/utils/sentry';
import ContactSupport from '@@/common/components/ContactSupport';
import Modal from '@@/common/components/Modal';

type TGenericError = Partial<UnionToIntersection<TErrorType>>;

const mapStateToProps = (state: TAppStoreState) => {
  return {
    isLoggedIn: !!state.oktaAccessToken,
    oktaAccessToken: state.oktaAccessToken,
    oktaIdToken: state.oktaIdToken,
    user: state.user,
    isCcpaBannerOpen: state.isCcpaBannerOpen,
    error: null,
  };
};

export type TErrorHandlerComponent = {
  history: History<any>;
} & ReturnType<typeof mapStateToProps>;

export type TErrorHandlerComponentState = {
  error: TGenericError | null;
  hasError: boolean;
  isErrorModalOpen: boolean;
  sentryLastEventId: ReturnType<typeof Sentry.lastEventId>;
};

export type TWithGlobals = {
  onError: (error: TErrorType, info?: any) => void;
} & ReturnType<typeof mapStateToProps>;

export default function withGlobals(
  WrappedComponent: ComponentType<TWithGlobals>
) {
  class ErrorHandlerComponent extends Component<
    TErrorHandlerComponent,
    TErrorHandlerComponentState
  > {
    constructor(props: TErrorHandlerComponent) {
      super(props);

      this.state = {
        error: null,
        hasError: false,
        isErrorModalOpen: false,
        sentryLastEventId: undefined,
      };
    }

    componentDidCatch(error: TGenericError, info: any) {
      captureException({ exception: error as Error, context: info });
      console.error('Error Boundary', error, info);
      this.setState({
        hasError: true,
        sentryLastEventId: Sentry.lastEventId(),
      });
    }

    handleDismissModal = () => {
      this.setState({
        isErrorModalOpen: false,
      });
    };

    handleError = (error: TGenericError, info?: string) => {
      if (!error) {
        return;
      }

      const { history } = this.props;
      captureException({
        exception: error as Error,
        context: { context: info },
      });

      const redirectTo = this.getErrorRedirectPath(error);
      if (redirectTo) {
        history.push(redirectTo);
      } else {
        this.setState({
          error: this.setCustomFieldsForError(error),
          isErrorModalOpen: true,
          sentryLastEventId: Sentry.lastEventId(),
        });
      }
    };

    get modalTitle() {
      const { error } = this.state;
      switch (error?.code) {
        case 'policy_cannot_be_altered':
        case 'update_already_in_progress':
          return 'Coverage Edit Temporarily Unavailable';
        case 'address_excluded':
          return "Sorry, Toggle doesn't cover your new address.";
        default:
          return 'Something went wrong';
      }
    }

    get modalBody() {
      const { error } = this.state;
      switch (error?.code) {
        case 'policy_cannot_be_altered':
        case 'address_excluded':
          return (
            <>
              <p>{error?.message}</p>
              {!!error?.quoteUnavailableReason && (
                <p data-testid="RestrictionErrorModal_CustomMessage">
                  {error.quoteUnavailableReason}
                </p>
              )}
            </>
          );
        case 'update_already_in_progress':
          return (
            <p data-testid="LockedPolicyErrorModal">
              Sorry, changes cannot be saved at this time. Please try again
              later.
            </p>
          );
        default:
          return (
            <>
              <p>{error?.message || <ContactSupport />}</p>
              {!!error?.fields && (
                <ul className="preserve_default_browser_styles">
                  {Object.entries(error.fields).map(([key, fieldErrorMsg]) => (
                    <li key={key}>
                      {startCase(key)}: {fieldErrorMsg}
                    </li>
                  ))}
                </ul>
              )}
            </>
          );
      }
    }

    getErrorRedirectPath = (error: TGenericError) => {
      const errorCodeToNewPath: Record<string, string> = {
        application_ineligible: '/ineligible',
        ineligible_pet_bite_history: '/ineligible?code=bite_history',
        ineligible_loss_history: '/ineligible?code=loss_history',
        invalid_login_creds: '/logout?invalid_session=1',
        unauthorized_request: '/logout?invalid_session=1',
      };

      return errorCodeToNewPath[error.code ?? ''];
    };

    setCustomFieldsForError = (error: TGenericError) => {
      const errorCodeToCustomKeys: Record<string, {}> = {
        payment_method_failed: {
          message:
            'We’d love to have you join us, but your credit card was declined',
        },
        policy_cannot_be_altered: {
          quoteUnavailableReason: error.message,
          message:
            'We’d love to toggle with you, but coverage editing is temporarily unavailable in your area. Please check back with us soon.',
        },
        address_excluded: {
          quoteUnavailableReason: error.message,
          message: 'Sorry, Toggle doesn’t cover your new address',
        },
      };

      return Object.assign(
        error,
        errorCodeToCustomKeys[error.code ?? ''] ?? {}
      );
    };

    render() {
      const { hasError, isErrorModalOpen, sentryLastEventId } = this.state;

      return (
        <>
          {hasError ? (
            <ErrorBoundaryView />
          ) : (
            <WrappedComponent onError={this.handleError} {...this.props} />
          )}
          <Modal
            className="ErrorModal"
            isOpen={isErrorModalOpen}
            onRequestClose={this.handleDismissModal}
            title={this.modalTitle}
            hasFooter
            primaryFooterButton={{
              type: 'button',
              onClick: this.handleDismissModal,
              label: 'Close',
            }}
          >
            {this.modalBody}
          </Modal>
          {process.env.REACT_APP_ENV !== 'production' && sentryLastEventId && (
            <DebugHelper sentryLastEventId={sentryLastEventId} />
          )}
        </>
      );
    }
  }

  const ComponentWithStatics = hoistStatics(
    ErrorHandlerComponent,
    WrappedComponent
  );
  const enhance = compose<ComponentClass<any, any>>(
    withRouter,
    connect(mapStateToProps)
  );
  const FinalComponent = enhance(ComponentWithStatics);

  FinalComponent.displayName = `withGlobals(${
    WrappedComponent.displayName || WrappedComponent.name
  })`;

  return FinalComponent;
}
